import { loadStripe } from '@stripe/stripe-js';
import { Cart } from './cart';

// Get Stripe publishable key from environment variables
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Initialize Stripe promise
let stripePromise: ReturnType<typeof loadStripe> | null = null;

// Get Stripe instance for Elements
export const getStripeElements = () => {
  if (!stripePublishableKey) {
    console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set');
    return null;
  }
  
  if (!stripePromise) {
    stripePromise = loadStripe(stripePublishableKey);
  }
  return stripePromise;
};

// Get CSRF token
const getCsrfToken = async (): Promise<string> => {
  try {
    // Try to get token from cookie directly
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return value;
      }
    }
    
    // If not found, try to fetch a new one from the server
    const response = await fetch('/api/csrf-token');
    
    if (!response.ok) {
      console.error(`Failed to get CSRF token: ${response.status} ${response.statusText}`);
      return '';
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.error('CSRF token endpoint did not return JSON');
      return '';
    }
    
    const data = await response.json();
    return data.token || '';
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return '';
  }
};

// Create Payment Intent for Elements
export const createPaymentIntent = async (cart: Cart, customerData?: { email?: string }) => {
  try {
    const csrfToken = await getCsrfToken();
    
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': csrfToken || '',
      },
      body: JSON.stringify({
        // SECURITY: Only send product IDs and quantities
        // Server will validate all product data (prices, availability, etc.)
        items: cart.items.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          size: item.size, // Include size for server validation
        })),
        currency: 'usd',
        customer_email: customerData?.email,
        metadata: {
          cartItemCount: cart.items.length.toString(),
          userId: localStorage.getItem('userId') || '',
          timestamp: new Date().toISOString(),
        },
      }),
    });

    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status} ${response.statusText}`);
      } else {
        const errorText = await response.text();
        console.error('Non-JSON error response:', errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
    }

    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Invalid response format from server');
    }
    
    const data = await response.json();
    
    if (!data.client_secret) {
      throw new Error('Failed to create payment intent');
    }

    return data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

// Calculate total amount including tax and shipping
export const calculateOrderTotal = (cart: Cart) => {
  const subtotal = cart.totalPrice;
  const shippingCost = subtotal > 100 ? 0 : 10; // Free shipping over $100
  const taxRate = 0.08; // 8% tax rate
  const taxAmount = subtotal * taxRate;
  
  return {
    subtotal,
    shipping: shippingCost,
    tax: taxAmount,
    total: subtotal + shippingCost + taxAmount,
  };
};

export default {
  getStripeElements,
  createPaymentIntent,
  calculateOrderTotal,
}; 